import React, { useContext, useEffect, useState } from 'react';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Grid,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import { MenuContext } from '../../context/Menu';
import { MenuItemType } from './Type';
import MenuRoutes from './MenuList';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import OrganizationContainer from './OrgInfo';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { hasAdminAccess, userLogin } from '@Services/hasAdminAccess';
import { CheckCondition, isRouteAllowed } from '../../utils/allowedActions';
import UserMenuRoutes from './UserMenuList';

// --------------------
// Styled Components
// --------------------

const MenuContainer = styled(Grid)<{ isMenuOpen?: boolean }>(({ theme, isMenuOpen }) => ({
  height: isMenuOpen ? 'calc(100vh - 164px)' : 'calc(100vh - 120px)',
  overflowY: 'auto',
  overflowX: 'hidden',

  '&::-webkit-scrollbar': {
    width: '0px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.secondary.main,
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.8),
  },
}));

const MenuContentWrapper = styled('div')(() => ({
  padding: '16px',
  paddingTop: '8px',
  width: '100%',
}));

const ListItem = styled('div')<{ isMenuOpen?: boolean; active?: boolean }>(({ theme, isMenuOpen, active }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  padding: isMenuOpen ? '12px 16px' : '12px',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  cursor: 'pointer',

  backgroundColor: active ? alpha(theme.palette.secondary.main, 0.12) : 'transparent',

  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  },

  // 🔵 LEFT ACTIVE BAR (like your image)
  ...(active && {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '0%',
      bottom: '0%',
      width: '6px',
      borderTopRightRadius: '10px',
      borderBottomRightRadius: '10px',
      backgroundColor: theme.palette.secondary.main,
    },
  }),
}));

const MenuItemWrapper = styled(Accordion)(() => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  margin: '0 !important',
  borderRadius: '8px',
  overflow: 'hidden',

  '&:before': {
    display: 'none',
  },

  '&.Mui-expanded': {
    margin: '0 !important',
  },
}));

const MenuItemLabel = styled('span')(({ theme }) => ({
  marginLeft: '16px',
  fontWeight: 500,
  fontSize: '14px',
  color: theme.palette.text.primary,
  flex: 1,
}));

const NestedMenuItem = styled('li')<{ active?: boolean }>(({ theme, active }) => ({
  width: '100%',
  height: '40px',
  padding: '10px 16px 10px 56px',
  cursor: 'pointer',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s ease',
  margin: '2px 0',
  position: 'relative',

  backgroundColor: active ? alpha(theme.palette.secondary.main, 0.12) : 'transparent',

  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.06),
  },

  // ✅ data-active support
  '&[data-active="true"]': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
    fontWeight: 500,
    color: theme.palette.secondary.main,

    // 🔵 LEFT BAR for data-active
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 8,
      top: '20%',
      bottom: '20%',
      width: '4px',
      borderTopRightRadius: '10px',
      borderRadius: '10px',
      backgroundColor: theme.palette.secondary.main,
    },
  },

  // ✅ active prop support
  ...(active && {
    color: theme.palette.secondary.main,
    fontWeight: 500,

    '&::before': {
      content: '""',
      position: 'absolute',
      left: 8,
      top: '20%',
      bottom: '20%',
      width: '4px',
      borderBottomRightRadius: '10px',
      borderTopRadius: '10px',
      backgroundColor: theme.palette.secondary.main,
    },
  }),
}));

const CompactIconContainer = styled('div')<{
  active?: boolean;
}>(({ theme, active }) => ({
  position: 'relative',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.25s ease',

  backgroundColor: active ? theme.palette.secondary.main : alpha(theme.palette.secondary.main, 0.08),

  color: active ? theme.palette.common.white : theme.palette.secondary.main,

  '& svg': {
    fontSize: '26px',
    zIndex: 2,
  },

  /* 🔵 Ring */
  ...(active && {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: '-6px',
      borderRadius: '50%',
      border: `2px solid ${alpha(theme.palette.secondary.main, 0.6)}`,
      animation: 'pulseRing 1.8s ease-out infinite',
    },
  }),

  '&:hover': {
    transform: 'scale(1.08)',
  },

  /* 🔑 Keyframes */
  '@keyframes pulseRing': {
    '0%': {
      transform: 'scale(0.9)',
      opacity: 0.9,
    },
    '70%': {
      transform: 'scale(1.1)',
      opacity: 0,
    },
    '100%': {
      opacity: 0,
    },
  },
}));

const ToggleButtonContainer = styled(IconButton)<{ active?: boolean }>(({ theme, active }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  color: theme.palette.secondary.main,

  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.15),
    transform: 'scale(1.08)',
  },

  /* 🔵 Active / Pulse ring */
  ...(active && {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: '-6px',
      borderRadius: '50%',
      border: `2px solid ${alpha(theme.palette.secondary.main, 0.6)}`,
      animation: 'pulseRing 1.8s ease-out infinite',
    },
  }),

  '@keyframes pulseRing': {
    '0%': { transform: 'scale(0.9)', opacity: 0.9 },
    '70%': { transform: 'scale(1.1)', opacity: 0 },
    '100%': { opacity: 0 },
  },
}));

const PopoverMenuList = styled(List)(() => ({
  width: '200px',
  padding: '8px',
}));

const PopoverMenuItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: '6px',
  margin: '2px 0',
  padding: '8px 12px',

  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  },

  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.16),
    },
  },
}));

// --------------------
// Menu Item Component
// --------------------

function MenuItem(props: MenuItemType) {
  const { isMenuOpen, Icon, value, nestedPaths } = props;
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const loginUser = useSelector((state: RootStateType) => state.loginUser);
  const isPrepaid = loginUser?.profile?._accountId?.isPrepaid;
  const isAdmin = !userLogin();

  const hideWallet = !(!isAdmin && isPrepaid);
  const hideCredit = !(!isAdmin && !isPrepaid);

  const isActive = nestedPaths?.some(
    path => location.pathname === path.pathURL || location.pathname.startsWith(path.pathURL + '/'),
  );

  const filteredNestedPaths = nestedPaths?.filter(pathObj => {
    if (
      (hideWallet && ['/transactions/wallet'].includes(pathObj.pathURL)) ||
      (hideCredit && ['/transactions/credit'].includes(pathObj.pathURL)) ||
      (!isAdmin && ['/transactions/credits-and-wallets', '/transactions/list'].includes(pathObj.pathURL))
    ) {
      return false;
    }

    if (isAdmin && ['/my-rates'].includes(pathObj.pathURL)) {
      return false;
    }

    return true;
  });

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!isMenuOpen && filteredNestedPaths?.length) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleNestedItemClick = (pathURL: string) => {
    navigate(pathURL);
    handlePopoverClose();
  };

  // -------- Compact Menu with Popover --------
  if (!isMenuOpen) {
    return (
      <>
        <Tooltip
          title={value}
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: '13px',
                padding: '6px 10px',
                borderRadius: '8px',
                animation: 'slideIn 180ms ease-out',
              },
            },
            arrow: {
              sx: {
                color: 'grey.900',
              },
            },
          }}
        >
          <div
            style={{
              margin: '12px 0',
              display: 'flex',
              justifyContent: 'center',
            }}
            onClick={handlePopoverOpen}
          >
            <CompactIconContainer active={isActive}>
              <Icon />
            </CompactIconContainer>
          </div>
        </Tooltip>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              boxShadow: theme.shadows[3],
              borderRadius: '12px',
            },
          }}
        >
          <PopoverMenuList>
            {filteredNestedPaths?.map((pathObj, index) => {
              const isNestedActive =
                location.pathname === pathObj.pathURL || location.pathname.startsWith(pathObj.pathURL + '/');

              return (
                <PopoverMenuItem
                  key={`${pathObj.pathURL}-${index}`}
                  selected={isNestedActive}
                  onClick={() => handleNestedItemClick(pathObj.pathURL)}
                >
                  <ListItemText
                    primary={pathObj.label}
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: isNestedActive ? 500 : 400,
                      color: isNestedActive ? theme.palette.secondary.main : 'inherit',
                    }}
                  />
                </PopoverMenuItem>
              );
            })}
          </PopoverMenuList>
        </Popover>
      </>
    );
  }

  // -------- Expanded Menu --------
  return (
    <MenuItemWrapper>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        sx={{
          padding: 0,
          margin: 0,
          minHeight: '48px',
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
          },
        }}
      >
        <ListItem
          isMenuOpen={isMenuOpen}
          active={isActive}
          style={{
            flex: 1,
          }}
        >
          <Icon
            style={{
              width: '20px',
              height: '20px',
              color: isActive ? theme.palette.secondary.main : 'inherit',
            }}
          />

          <MenuItemLabel
            style={{
              color: isActive ? theme.palette.secondary.main : 'inherit',
            }}
          >
            {value}
          </MenuItemLabel>
        </ListItem>
      </AccordionSummary>

      <AccordionDetails sx={{ padding: '8px 0' }}>
        <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
          {filteredNestedPaths?.map((pathObj, index) => {
            const isNestedActive =
              location.pathname === pathObj.pathURL || location.pathname.startsWith(pathObj.pathURL + '/');

            return (
              <Link
                key={`${pathObj.pathURL}-${index}`}
                to={pathObj.pathURL}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <NestedMenuItem data-active={isNestedActive}>
                  <KeyboardArrowRightIcon
                    sx={{
                      fontSize: 16,
                      mr: 1,
                      opacity: 0.6,
                      color: isNestedActive ? theme.palette.secondary.main : 'inherit',
                    }}
                  />
                  {pathObj.label}
                </NestedMenuItem>
              </Link>
            );
          })}
        </ul>
      </AccordionDetails>
    </MenuItemWrapper>
  );
}

// --------------------
// Main Menu Component
// --------------------

function Menu() {
  const { isMenuOpen, menuWidth, setIsMenuOpen } = useContext(MenuContext);

  const { PagesData, accessGroupById, SideNavbarPagesIds, SideNavbarActionsIds } = useSelector(
    (state: RootStateType) => state.accessGroup,
  );

  const { profile } = useSelector((state: RootStateType) => state.loginUser);

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationData);

  const { name = '' } = organizationData || {};

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isUserLogin = userLogin();
  const isAdmin = hasAdminAccess();

  const filteredPagesData = PagesData.filter(page => SideNavbarPagesIds.includes(page._id));

  const handleToggleMenu = () => {
    if (setIsMenuOpen) {
      setIsMenuOpen(prev => !prev);
    }
  };

  useEffect(() => {
    const check = CheckCondition(pathname);

    if (profile?.accessGroup === 'pu_all') {
      let matchFound = false;

      for (const menuRoute of UserMenuRoutes) {
        for (const nestedPath of menuRoute.nestedPaths) {
          if (pathname.toLowerCase().startsWith(nestedPath.pathURL.toLowerCase())) {
            matchFound = true;
            break;
          }
        }
        if (matchFound) break;
      }

      if (!matchFound && pathname !== '/') {
        navigate('/');
      }
    } else {
      if (check) {
        const actions = accessGroupById?.actions || [];

        if (actions.length > 0) {
          const res = isRouteAllowed(pathname, actions);
          if (!res) navigate(-1);
        }
      }
    }
  }, [accessGroupById?.pagesId, PagesData]);

  return (
    <>
      <MenuContainer container isMenuOpen={isMenuOpen}>
        <MenuContentWrapper>
          {!isUserLogin ? (
            <>
              {isAdmin ? (
                <>
                  {MenuRoutes.filter(menu => {
                    if (name === 'Cargo Solutions Network' && menu.label === 'Squad') {
                      return false;
                    }
                    return true;
                  }).map(menu => (
                    <MenuItem
                      key={menu.label}
                      isMenuOpen={isMenuOpen}
                      menuWidth={menuWidth || 308}
                      value={menu.label}
                      Icon={menu.Icon}
                      nestedPaths={menu.nestedPaths}
                    />
                  ))}
                </>
              ) : (
                <>
                  {MenuRoutes.map(menu => {
                    const filteredNestedPaths = menu?.nestedPaths?.filter(nestedPath => {
                      // always convert to array for uniform handling
                      const actions = Array.isArray(nestedPath?.matchActionName)
                        ? nestedPath.matchActionName
                        : [nestedPath?.matchActionName];

                      if (menu.label === 'Invoice') {
                        return filteredPagesData?.some(page =>
                          actions.some(action => page?.name?.toLowerCase().startsWith(action?.toLowerCase())),
                        );
                      } else {
                        return (
                          filteredPagesData?.some(page => actions.some(action => page?.name === action)) ||
                          SideNavbarActionsIds.some(item =>
                            actions.some(action => item.details?.name?.toLowerCase().includes(action?.toLowerCase())),
                          )
                        );
                      }
                    });

                    if (filteredNestedPaths?.length) {
                      return (
                        <MenuItem
                          key={menu.label}
                          isMenuOpen={isMenuOpen}
                          menuWidth={menuWidth || 308}
                          value={menu.label}
                          Icon={menu.Icon}
                          nestedPaths={filteredNestedPaths}
                        />
                      );
                    }

                    return null;
                  })}
                </>
              )}
            </>
          ) : (
            <>
              {UserMenuRoutes.map(menu => {
                const filteredNestedPaths = menu.nestedPaths.filter(
                  nestedPath => !(name === 'Cargo Solutions Network' && nestedPath.pathURL === '/my-rates'),
                );

                return (
                  <MenuItem
                    key={menu.label}
                    isMenuOpen={isMenuOpen}
                    menuWidth={menuWidth || 308}
                    value={menu.label}
                    Icon={menu.Icon}
                    nestedPaths={filteredNestedPaths}
                  />
                );
              })}
            </>
          )}

          <ToggleButtonContainer onClick={handleToggleMenu} active={!isMenuOpen} sx={{ alignSelf: 'center', mt: 4 }}>
            {isMenuOpen ? <ChevronLeftIcon /> : <MenuIcon />}
          </ToggleButtonContainer>
        </MenuContentWrapper>

        {isMenuOpen && <OrganizationContainer />}
      </MenuContainer>
    </>
  );
}

export default Menu;
